"use client";

import { useCallback, useEffect, type MutableRefObject, type Dispatch, type SetStateAction } from "react";
import {
  createLobbyConnection,
  createTableConnection,
  type PokerHubConnection,
  type LobbyHubEvents,
  type TableHubEvents,
  type ReactionDto,
} from "@/lib/texas-holdem-signalr";
import type {
  RoomDto,
  PokerTableView,
  ChatMessageDto,
  GameLogEntry,
  LastHandResultView,
  EndGameSummary,
  TournamentEliminationDto,
  BlindScheduleDto,
} from "@/lib/texas-holdem-types";
import { getSavedSession, saveSession, clearSession } from "@/lib/poker-session";
import type { ConnectionStatus } from "./use-texas-holdem";

export interface PokerConnectionDeps {
  lobbyRef: MutableRefObject<PokerHubConnection<LobbyHubEvents> | null>;
  tableRef: MutableRefObject<PokerHubConnection<TableHubEvents> | null>;
  deviceId: MutableRefObject<string>;
  playerIdRef: MutableRefObject<string | null>;
  pendingBlindScheduleRef: MutableRefObject<BlindScheduleDto | null>;
  setRoom: Dispatch<SetStateAction<RoomDto | null>>;
  setTableView: Dispatch<SetStateAction<PokerTableView | null>>;
  setChatMessages: Dispatch<SetStateAction<ChatMessageDto[]>>;
  setGameLog: Dispatch<SetStateAction<GameLogEntry[]>>;
  setLastResult: Dispatch<SetStateAction<LastHandResultView | null>>;
  setEndGameSummary: Dispatch<SetStateAction<EndGameSummary | null>>;
  setError: Dispatch<SetStateAction<string | null>>;
  setConnectionStatus: Dispatch<SetStateAction<ConnectionStatus>>;
  setPlayerId: Dispatch<SetStateAction<string | null>>;
  setPlayerName: Dispatch<SetStateAction<string>>;
  setIsHost: Dispatch<SetStateAction<boolean>>;
  setGameStarting: Dispatch<SetStateAction<boolean>>;
  setReactions: Dispatch<SetStateAction<(ReactionDto & { id: string })[]>>;
  setLastElimination: Dispatch<SetStateAction<TournamentEliminationDto | null>>;
  setSavedSessionRoomCode: Dispatch<SetStateAction<string | null>>;
}

export function usePokerConnection(deps: PokerConnectionDeps) {
  const {
    lobbyRef, tableRef, deviceId, playerIdRef, pendingBlindScheduleRef,
    setRoom, setTableView, setChatMessages, setGameLog, setLastResult,
    setEndGameSummary, setError, setConnectionStatus, setPlayerId,
    setPlayerName, setIsHost, setGameStarting, setReactions,
    setLastElimination, setSavedSessionRoomCode,
  } = deps;

  const tableInvoke = useCallback(async <T = void>(method: string, ...args: unknown[]): Promise<T | undefined> => {
    if (!tableRef.current) return undefined;
    try {
      return await tableRef.current.invoke<T>(method, ...args);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (!msg.includes("invocation canceled") && !msg.includes("connection is closing")) {
        setError(msg);
      }
      return undefined;
    }
  }, [tableRef, setError]);

  const setupLobbyHandlers = useCallback((lobby: PokerHubConnection<LobbyHubEvents>) => {
    lobby.on("PlayerJoined", (player) => {
      setRoom((prev) => {
        if (!prev) return prev;
        const exists = prev.players.some((p) => p.id === player.id);
        if (exists) return prev;
        return { ...prev, players: [...prev.players, { ...player, isConnected: true, seatIndex: -1 }] };
      });
    });

    lobby.on("PlayerLeft", (pid) => {
      setRoom((prev) => {
        if (!prev) return prev;
        return { ...prev, players: prev.players.filter((p) => p.id !== pid) };
      });
    });

    lobby.on("PlayerKicked", (pid) => {
      if (pid === playerIdRef.current) {
        setError("You have been removed from this table");
        setRoom(null);
        clearSession();
        try { lobby.disconnect(); } catch {}
        return;
      }
      setRoom((prev) => {
        if (!prev) return prev;
        return { ...prev, players: prev.players.filter((p) => p.id !== pid) };
      });
    });

    lobby.on("PlayerDisconnected", (pid) => {
      setRoom((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          players: prev.players.map((p) => (p.id === pid ? { ...p, isConnected: false } : p)),
        };
      });
    });

    lobby.on("GameStarting", () => {
      setGameStarting(true);
    });

    lobby.on("Error", (msg) => {
      setError(msg);
    });

    lobby.onReconnecting(() => setConnectionStatus("reconnecting"));
    lobby.onReconnected(async () => {
      setConnectionStatus("connected");
      const session = getSavedSession();
      if (session) {
        try {
          const result = await lobby.invoke<RoomDto | null>("JoinRoom", {
            roomCode: session.roomCode,
            playerName: session.playerName,
            deviceId: deviceId.current,
            timestamp: Date.now(),
            avatarUrl: typeof window !== "undefined" ? localStorage.getItem("poker_avatar") || undefined : undefined,
          });
          if (result) setRoom(result);
        } catch {}
      }
    });
    lobby.onClose(() => setConnectionStatus("disconnected"));
  }, [playerIdRef, deviceId, setRoom, setError, setGameStarting, setConnectionStatus]);

  const setupTableHandlers = useCallback((table: PokerHubConnection<TableHubEvents>) => {
    table.on("TableUpdated", (view) => {
      setTableView(view);
      setLastResult(view.lastHandResult);
    });

    table.on("GameLog", (entry) => {
      setGameLog((prev) => [...prev.slice(-200), entry]);
    });

    table.on("HandComplete", (result) => {
      setLastResult(result);
    });

    table.on("ChatMessage", (msg) => {
      setChatMessages((prev) => {
        const isDuplicate = prev.some(
          (m) => m.playerId === msg.playerId && m.message === msg.message
            && Math.abs(new Date(m.timestamp).getTime() - new Date(msg.timestamp).getTime()) < 3000
        );
        if (isDuplicate) return prev;
        return [...prev.slice(-100), msg];
      });
    });

    table.on("PlayerConnected", () => {});

    table.on("ReceiveReaction", (reaction) => {
      const id = `reaction-${Date.now()}-${reaction.fromSeat}`;
      setReactions((prev) => [...prev.slice(-10), { ...reaction, id }]);
      setTimeout(() => setReactions((prev) => prev.filter((r) => r.id !== id)), 2000);
    });

    table.on("PlayerEliminated", (elimination) => {
      setLastElimination(elimination);
      setTimeout(() => setLastElimination(null), 5000);
    });

    table.on("GameEnded", (summary) => {
      setEndGameSummary(summary);
      setTableView(null);
      setGameStarting(false);
    });

    table.on("Error", (msg) => {
      setError(msg);
    });

    table.onReconnecting(() => setConnectionStatus("reconnecting"));
    table.onReconnected(async () => {
      setConnectionStatus("connected");
      try {
        const session = getSavedSession();
        const pid = playerIdRef.current;
        if (pid && session?.roomCode) {
          setGameLog([]);
          const freshView = await table.invoke<PokerTableView | null>("JoinTable", session.roomCode, pid, deviceId.current);
          if (freshView) setTableView(freshView);
        }
      } catch {}
    });
    table.onClose(() => setConnectionStatus("disconnected"));
  }, [playerIdRef, deviceId, setTableView, setLastResult, setGameLog, setChatMessages, setReactions, setLastElimination, setEndGameSummary, setError, setGameStarting, setConnectionStatus]);

  const connectToTable = useCallback(
    async (roomCode: string, pid: string) => {
      setConnectionStatus("connecting");

      if (tableRef.current) {
        try { await tableRef.current.disconnect(); } catch {}
      }

      const table = createTableConnection();
      setupTableHandlers(table);

      try {
        await table.connect();
        tableRef.current = table;
        setConnectionStatus("connected");

        const view = await table.invoke<PokerTableView | null>("JoinTable", roomCode, pid, deviceId.current);
        if (view) {
          setTableView(view);
          setGameStarting(false);

          if (pendingBlindScheduleRef.current) {
            try {
              await table.invoke("UpdateSettings", { blindSchedule: pendingBlindScheduleRef.current });
            } catch {}
            pendingBlindScheduleRef.current = null;
          }
        }

        return view;
      } catch {
        tableRef.current = null;
        setConnectionStatus("disconnected");
        setError("Failed to connect to table");
        return null;
      }
    },
    [tableRef, deviceId, pendingBlindScheduleRef, setupTableHandlers, setConnectionStatus, setTableView, setGameStarting, setError]
  );

  const reconnect = useCallback(async (): Promise<"table" | "lobby" | null> => {
    const session = getSavedSession();
    if (!session) return null;

    setError(null);
    setPlayerId(session.playerId);
    setPlayerName(session.playerName);

    if (tableRef.current) {
      try { await tableRef.current.disconnect(); } catch {}
      tableRef.current = null;
    }
    if (lobbyRef.current) {
      try { await lobbyRef.current.disconnect(); } catch {}
      lobbyRef.current = null;
    }

    setConnectionStatus("connecting");

    const table = createTableConnection();
    tableRef.current = table;
    setupTableHandlers(table);

    try {
      await table.connect();

      const view = await table.invoke<PokerTableView | null>(
        "JoinTable",
        session.roomCode,
        session.playerId,
        deviceId.current
      );
      if (view) {
        setTableView(view);
        setConnectionStatus("connected");
        return "table";
      }
    } catch {}

    if (tableRef.current) {
      try { await tableRef.current.disconnect(); } catch {}
      tableRef.current = null;
    }

    try {
      const lobby = createLobbyConnection();
      lobbyRef.current = lobby;
      setupLobbyHandlers(lobby);

      await lobby.connect();

      const result = await lobby.invoke<RoomDto | null>("JoinRoom", {
        roomCode: session.roomCode,
        playerName: session.playerName,
        deviceId: deviceId.current,
        timestamp: Date.now(),
        avatarUrl: typeof window !== "undefined" ? localStorage.getItem("poker_avatar") || undefined : undefined,
      });

      if (result) {
        setRoom(result);
        const me = result.players.find((p) => p.id === session.playerId)
          ?? result.players.find((p) => p.name === session.playerName)
          ?? result.players[result.players.length - 1];
        if (me) {
          setPlayerId(me.id);
          setIsHost(me.isHost);
          saveSession({ roomCode: result.code, playerId: me.id, playerName: session.playerName });
          setSavedSessionRoomCode(result.code);
        }
        setConnectionStatus("connected");
        return "lobby";
      }
    } catch {}

    clearSession();
    setSavedSessionRoomCode(null);
    setError("Game is no longer available");
    setConnectionStatus("disconnected");
    return null;
  }, [tableRef, lobbyRef, deviceId, setupTableHandlers, setupLobbyHandlers, setError, setPlayerId, setPlayerName, setRoom, setIsHost, setConnectionStatus, setTableView, setSavedSessionRoomCode]);

  useEffect(() => {
    return () => {
      lobbyRef.current?.disconnect();
      tableRef.current?.disconnect();
    };
  }, [lobbyRef, tableRef]);

  return {
    tableInvoke,
    setupLobbyHandlers,
    connectToTable,
    reconnect,
  };
}
